"use client";

import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { useMessage } from "@/components/providers/MessageProvider";
import ButtonLoader from "@/components/shared/ButtonLoader";
import { CustomTextInput } from "@/components/shared/CustomTextInput";
import { Typography } from "@/components/shared/Typography";
import envConfig from "@/config/envConfig";
import { getISTDateString } from "@/utils/date";

const validationSchema = Yup.object({
  fullName: Yup.string()
    .trim()
    .required("Name is required")
    .min(5, "Name cannot be less than 5 characters")
    .max(30, "Name cannot be longer than 30 characters")
    .matches(/^[\p{L}\p{M} .'-]+$/u, "Name contains invalid characters"),
  mobileNumber: Yup.string()
    .trim()
    .matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit mobile number")
    .required("Mobile number is required"),
  selectedOptionIndex: Yup.number()
    .required("Please select an option")
    .oneOf([1, 2, 3, 4], "Invalid option"),
});

export default function DailyQuizContent({
  deviceId,
  quiz,
  date = getISTDateString(),
}: {
  deviceId: string;
  quiz: any;
  date?: string;
}) {
  const [formError, setFormError] = useState("");
  const [submitted, setSubmitted] = useState<0 | 1 | 2>(0);
  const { showMessage } = useMessage();

  const initialValues = useMemo(
    () => ({
      fullName: "",
      mobileNumber: "",
      selectedOptionIndex: undefined,
    }),
    [],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: false,
    onSubmit: async values => {
      if (!quiz) return;
      setFormError("");

      try {
        const response = await fetch(`${envConfig.publicServerUrl}/api/quiz/submit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Device-Id": localStorage.getItem("deviceId") || deviceId,
          },
          credentials: "include",
          body: JSON.stringify({
            name: values.fullName.trim(),
            mobile: String(values.mobileNumber),
            selectedOptionIndex: Number(values.selectedOptionIndex),
            quizDate: quiz?.quizDate,
            id: quiz?.id,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          setFormError(data?.error || "Submission failed");
          showMessage("error", data?.error || "Submission failed");
          return;
        } else {
          showMessage("success", "Your answer has been submitted successfully!");
          setSubmitted(data?.data == false ? 1 : 2);
        }
      } catch (err: any) {
        console.log("data", err);
        setFormError(err?.message || "Something went wrong");
      }
    },
    onReset: () => {
      setSubmitted(0);
    },
  });

  useEffect(() => {
    const localStorageDeviceId = localStorage.getItem("deviceId");
    if (!localStorageDeviceId) {
      localStorage.setItem("deviceId", deviceId);
    }
  }, [deviceId]);

  if (submitted === 1 || submitted === 2) {
    return (
      <div className="container my-16 max-w-[860px] mx-auto text-center">
        <div className="flex justify-center">
          <div className="flex items-center justify-center w-20 h-20 bg-green-100 rounded-full">
            <svg
              className="w-10 h-10 text-green-600"
              fill="none"
              stroke="currentColor"
              strokeWidth={3}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>

        <h2 className="mt-6 text-2xl font-bold text-green-700">Thank you for participating!</h2>

        <p className="mt-2 text-gray-600">Your answer has been submitted successfully.</p>
        {submitted == 2 && (
          <p className="mt-2 text-gray-600">
            Winners for the day have already been selected. But you still have a chance to win the
            bumper prize.
          </p>
        )}

        <button
          onClick={() => {
            formik.resetForm();
            setSubmitted(0);
          }}
          className="mt-6 bg-[#e80d40] px-6 py-3 text-white font-bold rounded"
        >
          Go to Home
        </button>
      </div>
    );
  }

  return (
    <div className="container my-8 max-w-[860px] mx-auto">
      {!quiz || quiz?.error ? (
        <Typography variant="h5" className="text-center">
          {quiz.error} {date && `( ${date} )`}
        </Typography>
      ) : (
        <>
          <div className="mt-4">
            <Typography variant="h4" className="text-center">
              {quiz?.question}
            </Typography>
          </div>

          <form onSubmit={formik.handleSubmit}>
            {/* OPTIONS */}

            <div className="mt-6 grid grid-cols-1 gap-2 ">
              {quiz?.options
                ?.filter((v: any) => !!v?.option?.trim())
                ?.map((opt: any, index: number) => (
                  <label
                    key={index}
                    className="flex items-center p-3 border rounded cursor-pointer hover:bg-slate-300 dark:hover:bg-black"
                  >
                    <input
                      type="radio"
                      name="selectedOptionIndex"
                      value={index + 1}
                      checked={Number(formik.values.selectedOptionIndex) == index + 1}
                      onChange={formik.handleChange}
                      className="mr-2 accent-[#e80d40]"
                    />

                    <Typography variant="body1">{opt.option}</Typography>
                  </label>
                ))}
            </div>

            {formik.touched.selectedOptionIndex && formik.errors.selectedOptionIndex && (
              <span className="text-red-500 text-sm">{formik.errors.selectedOptionIndex}</span>
            )}

            {/* USER FORM */}

            <div className="grid grid-cols-1 gap-4 mt-6 lg:grid-cols-2 ">
              <CustomTextInput
                placeHolder="പേര് / Name"
                id="fullName"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName ? formik.errors.fullName : undefined}
              />

              <CustomTextInput
                placeHolder="മൊബൈൽ നമ്പർ / Mobile Number"
                id="mobileNumber"
                type="number"
                value={formik.values.mobileNumber}
                onChange={formik.handleChange}
                error={formik.touched.mobileNumber && Boolean(formik.errors.mobileNumber)}
                helperText={formik.touched.mobileNumber ? formik.errors.mobileNumber : undefined}
              />
            </div>

            {formError && (
              <div className="mt-4">
                <span className="block text-md font-bold text-red-500">{formError}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="w-full mt-6 bg-[#e80d40] px-4 h-[55px] flex items-center justify-center text-white font-bold rounded"
            >
              {formik.isSubmitting ? <ButtonLoader /> : "സമർപ്പിക്കുക / Submit"}
            </button>
          </form>
        </>
      )}
    </div>
  );
}
